import { PureComponent } from 'react';
import { ButtonPrimary } from '@spotify-internal/encore-web';
import PropTypes from 'prop-types';

import { withUserInfo } from 'context/UserInfo/UserInfoProvider';
import { withMarketInfo } from 'context/MarketInfo/MarketInfoProvider';
import withTranslation from 'lib/shared/i18n/withTranslation';
import BackButton from 'components/ContentReporting/ContentLegalReporting/BackButton/BackButton';
import analytics, {
  ShowStepEventLabel,
} from 'components/ContentReporting/ContentLegalReporting/report-content-analytics-utils';
import {
  CLAIM_DETAILS,
  CONTENT_DETAILS,
  COPYRIGHT_TYPE_REQUIRED_CONTENT_TYPES,
  OPTIONS_CLAIM_TYPE,
  OPTIONS_CLAIMANT_RELATIONSHIP_RIGHTSHOLDER,
  RUSSIAN_MARKET_LIST,
  TERRITORY_LIST_GENERIC,
  TERRITORY_LIST_TRADEMARK,
  TERRITORY_OTHER,
} from 'components/ContentReporting/ContentLegalReporting/content-legal-reporting-config';
import { getFieldValue } from 'components/ContentReporting/form-utils';

import TrademarkContentTypeFields from './TrademarkContentTypeFields';
import CopyrightContentTypeFields from './CopyrightContentTypeFields';
import RightsholderFields from './RightsHolderFields';
import TerritoriesFields from './TerritoriesFields';

const renderNavigationButtons = (t, onSubmitButtonClick, onBackButtonClick, loading) => (
  <div style={{ display: 'flex' }}>
    <BackButton handleBackButton={onBackButtonClick} />
    <ButtonPrimary onClick={onSubmitButtonClick} disabled={loading}>
      {t('continue')}
    </ButtonPrimary>
  </div>
);

const isOtherTerritorySelected = fieldValue =>
  !!fieldValue(CLAIM_DETAILS.fields.CLAIM_TERRITORIES) &&
  !!fieldValue(CLAIM_DETAILS.fields.CLAIM_TERRITORIES)[TERRITORY_OTHER];

const isTrademarkClaim = fieldValue => fieldValue(CONTENT_DETAILS.fields.CLAIM_TYPE) === OPTIONS_CLAIM_TYPE.TRADEMARK;
const isCopyrightClaim = fieldValue => fieldValue(CONTENT_DETAILS.fields.CLAIM_TYPE) === OPTIONS_CLAIM_TYPE.COPYRIGHT;

const isCopyrightTypeSelectionNeeded = fieldValue =>
  isCopyrightClaim(fieldValue) &&
  COPYRIGHT_TYPE_REQUIRED_CONTENT_TYPES.includes(fieldValue(CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE));

const isAuthorizedAgentForRightsholder = fieldValue =>
  fieldValue(CLAIM_DETAILS.fields.CLAIMANT_RELATIONSHIP_RIGHTSHOLDER) ===
  OPTIONS_CLAIMANT_RELATIONSHIP_RIGHTSHOLDER.OTHER;

const getRightsholderFields = (fieldValue, market) => {
  const isRussiaMarket = RUSSIAN_MARKET_LIST.includes(market);
  const isAuthorizedAgentForRightsholderValue = isAuthorizedAgentForRightsholder(fieldValue);
  return [
    CLAIM_DETAILS.fields.CLAIMANT_RELATIONSHIP_RIGHTSHOLDER,
    CLAIM_DETAILS.fields.RIGHTSHOLDER_NAME,
    CLAIM_DETAILS.fields.RIGHTSHOLDER_EMAIL,
    CLAIM_DETAILS.fields.RIGHTSHOLDER_PHONE_FAX,
    CLAIM_DETAILS.fields.RIGHTSHOLDER_ROLE,
  ]
    .filter(
      f =>
        ![
          CLAIM_DETAILS.fields.RIGHTSHOLDER_NAME,
          CLAIM_DETAILS.fields.RIGHTSHOLDER_EMAIL,
          CLAIM_DETAILS.fields.RIGHTSHOLDER_ROLE,
          CLAIM_DETAILS.fields.RIGHTSHOLDER_PHONE_FAX,
        ].includes(f) || isAuthorizedAgentForRightsholderValue,
    )
    .filter(f => f !== CLAIM_DETAILS.fields.RIGHTSHOLDER_PHONE_FAX || isRussiaMarket);
};

const getCopyrightFields = fieldValue => {
  if (!isCopyrightClaim(fieldValue)) {
    return [];
  }

  return [CLAIM_DETAILS.fields.CLAIM_COPYRIGHT, CLAIM_DETAILS.fields.CLAIM_COPYRIGHT_INFO].filter(
    f => f !== CLAIM_DETAILS.fields.CLAIM_COPYRIGHT || isCopyrightTypeSelectionNeeded(fieldValue),
  );
};

const getTrademarkFields = fieldValue => {
  if (!isTrademarkClaim(fieldValue)) {
    return [];
  }
  return [CLAIM_DETAILS.fields.CLAIM_TRADEMARK_STATUS, CLAIM_DETAILS.fields.CLAIM_TRADEMARK_NO];
};

const getTerritoriesFields = fieldValue =>
  [CLAIM_DETAILS.fields.CLAIM_TERRITORIES, CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES].filter(
    f => f !== CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES || isOtherTerritorySelected(fieldValue),
  );

export const getPayloadFields = (fieldValue, market) => [
  ...getRightsholderFields(fieldValue, market),
  ...getCopyrightFields(fieldValue),
  ...getTrademarkFields(fieldValue),
  ...getTerritoriesFields(fieldValue),
];

class Step3OwnedContent extends PureComponent {
  static propTypes = {
    formData: PropTypes.object.isRequired,
    handleFieldChange: PropTypes.func.isRequired,
    renderErrorMessage: PropTypes.func.isRequired,
    forceValidation: PropTypes.bool.isRequired,
    goToNextStep: PropTypes.func.isRequired,
    handleBackButton: PropTypes.func.isRequired,
    renderLoadingSpinner: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    market: PropTypes.string.isRequired,
    loading: PropTypes.bool.isRequired,
    enabledFeatureFlags: PropTypes.instanceOf(Set).isRequired,
  };

  componentDidMount() {
    analytics.trackShowStepEvent(ShowStepEventLabel.Step3ContentInformation);
    window.scrollTo(0, 0);
  }

  render() {
    const {
      formData,
      handleFieldChange,
      renderErrorMessage,
      forceValidation,
      goToNextStep,
      handleBackButton,
      renderLoadingSpinner,
      t,
      market,
      loading,
      enabledFeatureFlags,
    } = this.props;
    const fieldValue = getFieldValue(formData);

    const trademarkClaim = isTrademarkClaim(fieldValue);
    const copyrightTypeSelectionNeeded = isCopyrightTypeSelectionNeeded(fieldValue);
    const copyrightInfoNeeded = isCopyrightClaim(fieldValue);
    const authorizedAgentForRightsholder = isAuthorizedAgentForRightsholder(fieldValue);
    const isRussiaMarket = RUSSIAN_MARKET_LIST.includes(market);

    const requiredFields = [
      CLAIM_DETAILS.fields.CLAIMANT_RELATIONSHIP_RIGHTSHOLDER,
      CLAIM_DETAILS.fields.CLAIM_TERRITORIES,
    ];

    if (authorizedAgentForRightsholder) {
      requiredFields.push(CLAIM_DETAILS.fields.RIGHTSHOLDER_NAME, CLAIM_DETAILS.fields.RIGHTSHOLDER_EMAIL);
      if (isRussiaMarket) {
        requiredFields.push(CLAIM_DETAILS.fields.RIGHTSHOLDER_PHONE_FAX);
      }
    }
    if (copyrightTypeSelectionNeeded) {
      requiredFields.push(CLAIM_DETAILS.fields.CLAIM_COPYRIGHT);
    }
    if (trademarkClaim) {
      requiredFields.push(CLAIM_DETAILS.fields.CLAIM_TRADEMARK_STATUS);
    }
    if (isOtherTerritorySelected(fieldValue)) {
      requiredFields.push(CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES);
    }
    if (copyrightInfoNeeded) {
      requiredFields.push(CLAIM_DETAILS.fields.CLAIM_COPYRIGHT_INFO);
    }

    return (
      <>
        <RightsholderFields
          handleFieldChange={handleFieldChange}
          formData={formData}
          forceValidation={forceValidation}
          isAuthorizedAgentForRightsholder={authorizedAgentForRightsholder}
          isRussiaMarket={isRussiaMarket}
          t={t}
        />

        {isCopyrightClaim(fieldValue) && (
          <CopyrightContentTypeFields
            handleFieldChange={handleFieldChange}
            formData={formData}
            forceValidation={forceValidation}
            shouldDisplayTypeSelection={copyrightTypeSelectionNeeded}
            t={t}
            enabledFeatureFlags={enabledFeatureFlags}
          />
        )}

        {trademarkClaim && (
          <TrademarkContentTypeFields
            handleFieldChange={handleFieldChange}
            formData={formData}
            forceValidation={forceValidation}
            t={t}
          />
        )}

        <TerritoriesFields
          handleFieldChange={handleFieldChange}
          formData={formData}
          territoryList={trademarkClaim ? TERRITORY_LIST_TRADEMARK : TERRITORY_LIST_GENERIC}
          isOtherTerritorySelected={isOtherTerritorySelected(fieldValue)}
          forceValidation={forceValidation}
        />

        {renderErrorMessage()}
        {renderLoadingSpinner()}
        {renderNavigationButtons(t, () => goToNextStep(requiredFields), handleBackButton, loading)}
      </>
    );
  }
}

export default withTranslation('infringement_claim_form')(withUserInfo(withMarketInfo(Step3OwnedContent)));
